import { ChangeDetectionStrategy, Component, Input, inject, ViewChildren, QueryList } from '@angular/core';
import { IndexCardsStore } from '../indexcards.store';
import { NgSelectComponent } from '@ng-select/ng-select';
import { RateColumn, RateField } from '../timesats-columns';
import { Grupper, IndexCards } from '../IndexCards';


@Component({
    selector: 'tr[app-medarbejder]',
    templateUrl: './medarbejder.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class MedarbejderComponent {
    readonly rateErrors = new Set<RateField>();
    readonly indexCardsStore = inject(IndexCardsStore);
    private lastGrupperStr: string | undefined;
    private cachedGrupperNumbers: number[] = [];

    @Input({ required: true }) user!: IndexCards;
    @Input({ required: true }) rateColumns: RateColumn[] = [];
    @ViewChildren(NgSelectComponent) selects!: QueryList<NgSelectComponent>;
    
    get initials(): string {
        const configured = this.user.initialer?.trim();
        const nameInitials = [this.user.fornavn?.trim()[0], this.user.efternavn?.trim()[0]].filter(Boolean).join('');
        const fallback = this.user.brugerId?.split(/[_\s.-]+/).filter(Boolean).pop() || '?';
        return (configured && configured.length <= 3 ? configured : nameInitials || fallback.slice(0, 2)).toLocaleUpperCase('da');
    }

    closeDropdowns(): void { this.selects?.forEach(select => select.close()); }

    groupLabel(group: Grupper): string {
        return group.gruppe?.trim() || `Gruppe ${group.id}`;
    }

    groupSummary(groups: Grupper[]): string {
        return groups.map(group => this.groupLabel(group)).join(', ');
    }

    getGrupperNumbers(): number[] {
        const currentStr = this.user?.grupper;
        if (currentStr !== this.lastGrupperStr) {
            this.lastGrupperStr = currentStr;
            this.cachedGrupperNumbers = currentStr?.match(/\d+/g)?.map(Number) ?? [];
        }
        return this.cachedGrupperNumbers;
    }

    updateTitel(cardId: number, titel: number) {
        this.indexCardsStore.updateIndexCard(cardId, 'titel', titel);
    }

    onTelefonDirektChange(cardId: number, telefondirekte: string): void {
        this.indexCardsStore.updateIndexCard(cardId, 'telefondirekte', telefondirekte);
    }

    onTelefonMobilChange(cardId: number, telefonmobil: string): void {
        this.indexCardsStore.updateIndexCard(cardId, 'telefonmobil', telefonmobil);
    }

    updateSagsbehandler(cardId: number, sagsbehandler: boolean): void {
        this.indexCardsStore.updateIndexCard(cardId, 'sagsbehandler', sagsbehandler);
    }

    updateKundeansvarlig(cardId: number, kundeansvarlig: boolean): void {
        this.indexCardsStore.updateIndexCard(cardId, 'kundeansvarlig', kundeansvarlig);
    }

    updateLedelsesansvarlig(cardId: number, ledelsesansvarlig: boolean): void {
        this.indexCardsStore.updateIndexCard(cardId, 'ledelsesansvarlig', ledelsesansvarlig);
    }

    onTimesatsChange(cardId: number, field: RateField, value: string): void {
        // Preserve the current whole-number contract; never truncate invalid input.
        const text = String(value ?? '').trim();
        if (text === '') {
            this.rateErrors.delete(field);
            this.indexCardsStore.updateIndexCard(cardId, field, null);
            return;
        }
        if (!/^\d+$/.test(text) || !Number.isSafeInteger(Number(text))) {
            this.rateErrors.add(field);
            return;
        }
        this.rateErrors.delete(field);
        this.indexCardsStore.updateIndexCard(cardId, field, Number(text));
    }

    updateLev(cardId: number, lev: number): void {
        this.indexCardsStore.updateIndexCard(cardId, 'lev', lev);
    }

    updateAfdeling(cardId: number, afdeling: number | null): void {
        this.indexCardsStore.updateIndexCard(cardId, 'afdeling', afdeling);
    }

    updateGrupper(cardId: number, selectedIds: readonly number[] | null | undefined): void {
        const joined = selectedIds?.length ? [...selectedIds].sort((a, b) => a - b).join('-') : '';
        this.indexCardsStore.updateIndexCard(cardId, 'grupper', joined);
    }

    onAnsaettelsesdatoChange(cardId: number, date: string): void {
        this.indexCardsStore.updateIndexCard(cardId, 'ansaettelsesdato', date as any);
    }

    onFoedselsdatoChange(cardId: number, date: string): void {
        this.indexCardsStore.updateIndexCard(cardId, 'foedselsdato', date as any);
    }

    onFratraadtChange(cardId: number, date: string): void {
        this.indexCardsStore.updateIndexCard(cardId, 'fratraadt', date as any);
    }

}
